/**
 * Pattern: H2 / L2 — Brooks's Second-Attempt Entry
 *
 * Al Brooks's framework: when the market is trending and pulls back,
 * the FIRST counter-trend attempt (H1 in an uptrend = first lower high
 * that fails; L1 in a downtrend = first higher low that fails) usually
 * fails. The SECOND attempt (H2 / L2) is the high-probability entry —
 * it's the spot where the counter-trend traders give up and the trend
 * resumes.
 *
 * Detection model:
 *   1. Find the most recent swing in the trade direction (long → recent
 *      swing LOW that's the trend's foundation; short → recent swing HIGH)
 *   2. Since that swing, count counter-direction pullback legs
 *   3. A "leg" = a 2+ bar move against the trend that's then reversed
 *   4. H2/L2 fires when the entry is on the SECOND counter-direction
 *      attempt's reversal back to the trend
 *
 * Simpler approximation used here (full Brooks H-counting is its own
 * subfield): count swings of the opposite type since the trend swing.
 * For a LONG: how many swing HIGHS exist between the last swing LOW
 * and the entry? 2 = H2.
 *
 * Pure function. No side effects.
 */

import { detectSwings } from '../swingDetect.js';

const normalizeDirection = (raw) => {
  if (!raw) return null;
  const s = String(raw).toLowerCase().trim();
  if (s === 'long' || s === 'buy' || s === 'l') return 'long';
  if (s === 'short' || s === 'sell' || s === 's') return 'short';
  return null;
};

const findEntryBarIdx = (bars, entryTs) => {
  if (!Array.isArray(bars) || bars.length === 0) return -1;
  let idx = -1;
  for (let i = 0; i < bars.length; i++) {
    const t = Number(bars[i]?.time);
    if (!Number.isFinite(t)) continue;
    if (t <= entryTs) idx = i;
    else break;
  }
  return idx;
};

export const H2L2_LOOKBACK_BARS = 30;

export const detectH2L2 = ({ bars, entryTs, direction, options = {} } = {}) => {
  const dir = normalizeDirection(direction);
  if (!dir) return null;
  if (!Array.isArray(bars) || bars.length === 0) return null;
  if (!Number.isFinite(entryTs)) return null;

  const lookback = options.lookbackBars || H2L2_LOOKBACK_BARS;

  const entryIdx = findEntryBarIdx(bars, entryTs);
  if (entryIdx < 10) return null;

  const windowStart = Math.max(0, entryIdx - lookback);
  const windowBars = bars.slice(windowStart, entryIdx + 1);
  const swings = detectSwings(windowBars);
  if (swings.length === 0) return null;

  // For a LONG: trend foundation is the most recent swing LOW.
  //   Then count swing HIGHS that occurred AFTER that low and before entry.
  //   If count === 2 → H2.
  // For a SHORT: trend foundation is most recent swing HIGH.
  //   Count swing LOWS after it. If count === 2 → L2.
  const trendSwingType = dir === 'long' ? 'low' : 'high';
  const pullbackSwingType = dir === 'long' ? 'high' : 'low';

  // Find the most recent trend-foundation swing
  let lastTrendIdx = -1;
  for (let i = swings.length - 1; i >= 0; i--) {
    if (swings[i].type === trendSwingType) {
      lastTrendIdx = i;
      break;
    }
  }
  if (lastTrendIdx < 0) return null;

  // Count pullback swings after the trend swing
  const pullbacks = [];
  for (let i = lastTrendIdx + 1; i < swings.length; i++) {
    if (swings[i].type === pullbackSwingType) pullbacks.push(swings[i]);
  }

  const legCount = pullbacks.length;
  // H2/L2 = second pullback attempt
  if (legCount !== 2) {
    return null;
  }

  return {
    type: dir === 'long' ? 'h2' : 'l2',
    score: 1,
    maxScore: 1,
    aligned: true,
    details: `${dir === 'long' ? 'H2' : 'L2'} — entry is the 2nd ${pullbackSwingType === 'high' ? 'lower-high' : 'higher-low'} attempt since the last swing ${trendSwingType}. First attempt failed; this is Brooks's high-probability second entry.`,
    evidence: {
      trendSwingType,
      pullbackSwingType,
      pullbackCount: legCount,
      trendSwingIndex: swings[lastTrendIdx].index + windowStart,
      lastPullbackIndex: pullbacks[pullbacks.length - 1].index + windowStart,
    },
  };
};
